import { Button, TableWrapper } from '@basqet-dashboard/common/components';
import { addDays, format } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (value) =>
  safeNumber(value).toLocaleString('en-US', { maximumFractionDigits: 8 });

const rateLine = (conversion) => {
  if (!conversion) return '';
  return `1 ${conversion.from_currency} = ${formatNumber(conversion.rate_used)} ${
    conversion.to_currency
  }`;
};

// Backend status → display label (figma node 13149:16384). DataTable
// derives the pill className from `item.status_display.toLowerCase()`,
// so the lowercase labels here line up with the .complete / .processing
// / .failed pill rules in table.scss (+ conversions.scss for processing).
const STATUS_DISPLAY = {
  SUCCESS: 'COMPLETE',
  INITIATED: 'PROCESSING',
  FAILED: 'FAILED'
};

// TableFilters' DATE_OPTIONS dropdown emits preset keys (`day`, `week`,
// `month`, `quarter`, `year`). The hook's filter expects a { from, to }
// range, so we translate at the boundary and keep the hook unchanged.
const presetToRange = (preset) => {
  if (!preset) return { from: '', to: '' };
  const today = new Date();
  const daysByPreset = { day: 1, week: 7, month: 30, quarter: 90, year: 365 };
  const days = daysByPreset[preset] ?? 0;
  if (!days) return { from: '', to: '' };
  return {
    from: format(addDays(today, -days), 'yyyy-MM-dd'),
    to: format(today, 'yyyy-MM-dd')
  };
};

// Excel/Sheets formula-injection mitigation: a cell starting with =, +,
// -, @, \t or \r is interpreted as a formula. Prefix with ' to neutralise.
const CSV_INJECTION_PREFIX = /^[=+\-@\t\r]/;
const csvCell = (raw) => {
  let value = String(raw ?? '');
  if (CSV_INJECTION_PREFIX.test(value)) value = `'${value}`;
  return `"${value.replace(/"/g, '""')}"`;
};

const downloadCsv = (conversions) => {
  const rows = [
    ['Conversion ID', 'From', 'To', 'Rate', 'Date', 'Status', 'Reference', 'Failure reason'],
    ...conversions.map((c) => [
      c.conversion_id,
      c.from_display,
      c.to_display,
      rateLine(c),
      c.created_at,
      c.status,
      c.reference ?? '',
      c.failure_reason ?? ''
    ])
  ];
  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `conversions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const RecentConversions = ({
  conversions,
  totalLoaded,
  isLoading,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  onRefresh,
  setSearchQuery,
  setStatusFilter,
  setDateFilter
}) => {
  // DataTable's row template renders the raw column value, so we shape
  // each row up-front: a precomputed rate string, a status_display that
  // matches the design pill labels, and the original record is kept
  // intact so the failure_reason tooltip survives.
  const tableRows = conversions.map((conversion) => ({
    ...conversion,
    rate_line: rateLine(conversion),
    status_display: STATUS_DISPLAY[conversion.status] ?? conversion.status
  }));

  // Clearing the search (via the in-field × in TableFilters) also refetches so
  // the merchant sees fresh server data, not just the loaded pages filtered down.
  const handleClearSearch = () => {
    setSearchQuery('');
    onRefresh();
  };

  const handleStatusChange = (displayValue) => {
    const reverseMap = { COMPLETE: 'SUCCESS', PROCESSING: 'INITIATED', FAILED: 'FAILED' };
    setStatusFilter(reverseMap[displayValue] ?? '');
  };

  const handleDateFilter = (preset) => {
    setDateFilter(presetToRange(preset));
  };

  const hasActiveFilter = Boolean(totalLoaded > conversions.length);

  return (
    <div className="recent-conversions">
      <TableWrapper
        data={tableRows}
        name="conversion"
        headerName="wallet_conversions"
        title="Recent"
        hasFilters
        onSearch={setSearchQuery}
        onClear={handleClearSearch}
        searchPlaceholder="Search for conversion ID, source currency or destination currency"
        onStatusChange={handleStatusChange}
        onDateFilter={handleDateFilter}
        isFetching={isLoading || isFetching}
        isLoading={isFetchingNextPage}
        canLoadMore={hasNextPage}
        onLoadMore={fetchNextPage}
        addon={
          <Button
            variant="white"
            size="small"
            onClick={() => downloadCsv(conversions)}
            disabled={conversions.length === 0}>
            Export File
          </Button>
        }
        emptyDataComponent={<EmptyConversions />}
      />

      {hasActiveFilter && hasNextPage && (
        <p className="recent-conversions__filter-hint" role="status">
          {`Showing ${conversions.length} matching row${
            conversions.length === 1 ? '' : 's'
          } from the ${totalLoaded} loaded so far. Load more to search older conversions.`}
        </p>
      )}
    </div>
  );
};

const EmptyConversions = () => (
  <div className="recent-conversions__empty">
    <p>No conversions yet.</p>
  </div>
);

RecentConversions.propTypes = {
  conversions: PropTypes.array.isRequired,
  totalLoaded: PropTypes.number,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  isFetchingNextPage: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  fetchNextPage: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  setSearchQuery: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
  setStatusFilter: PropTypes.func.isRequired,
  dateFilter: PropTypes.shape({
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired
  }),
  setDateFilter: PropTypes.func.isRequired
};

RecentConversions.defaultProps = {
  totalLoaded: 0,
  isLoading: false,
  isFetching: false,
  isFetchingNextPage: false,
  hasNextPage: false,
  onRefresh: () => {},
  statusFilter: '',
  dateFilter: { from: '', to: '' }
};

export { RecentConversions };
